import React, { useState, useEffect, useContext, useCallback } from 'react';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Calendar } from 'primereact/calendar';
import { Button } from 'primereact/button';
import axios from 'axios';
import { BarrioContext } from '../components/BarrioContext';
import { useAuth } from '../components/AuthContext';

const formatFecha = (fechaStr) => {
    if (!fechaStr) return '';
    const d = new Date(fechaStr);
    const hoy = new Date();
    const ayer = new Date(hoy);
    ayer.setDate(hoy.getDate() - 1);
    if (d.toDateString() === hoy.toDateString()) return 'Hoy';
    if (d.toDateString() === ayer.toDateString()) return 'Ayer';
    return d.toLocaleDateString('es-AR', { day: 'numeric', month: 'short' });
};

const toDateStr = (d) => {
    if (!d) return null;
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const ComunicadosBarrio = () => {
    const HOST_API = process.env.REACT_APP_URL;
    const { barrio } = useContext(BarrioContext);
    const { administracionId, usuarioId } = useAuth();
    const [comunicados, setComunicados] = useState([]);
    const [loading, setLoading] = useState(false);
    const [marcandoId, setMarcandoId] = useState(null);

    const [vistaHistorico, setVistaHistorico] = useState(false);
    const [fechaDesde, setFechaDesde] = useState(null);
    const [fechaHasta, setFechaHasta] = useState(null);

    const fetchComunicados = useCallback(async () => {
        if (!barrio?.id) return;
        setLoading(true);
        try {
            const params = {
                administracionId,
                barrioId: barrio.id,
                usuarioId,
                soloNoLeidos: !vistaHistorico,
            };
            if (vistaHistorico) {
                if (fechaDesde) params.fechaDesde = toDateStr(fechaDesde);
                if (fechaHasta) params.fechaHasta = toDateStr(fechaHasta);
            }
            const { data } = await axios.get(`${HOST_API}api/comunicados/GetComunicados`, { params });
            setComunicados(data);
        } catch (err) {
            console.error('Error al cargar comunicados:', err);
        } finally {
            setLoading(false);
        }
    }, [barrio?.id, HOST_API, administracionId, usuarioId, vistaHistorico, fechaDesde, fechaHasta]);

    useEffect(() => { fetchComunicados(); }, [fetchComunicados]);

    const handleMarcarLeido = async (comunicadoId) => {
        setMarcandoId(comunicadoId);
        try {
            await axios.put(`${HOST_API}api/comunicados/MarcarLeido?comunicadoId=${comunicadoId}&usuarioId=${usuarioId}`);
            setComunicados((prev) => prev.filter((c) => c.comunicadoId !== comunicadoId));
        } catch (err) {
            console.error('Error al marcar como leído:', err);
        } finally {
            setMarcandoId(null);
        }
    };

    return (
        <div style={{ width: '100%' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.75rem', marginBottom: '1.25rem' }}>
                <h4 style={{ margin: 0, color: '#101828' }}>{vistaHistorico ? 'Histórico de comunicados' : 'Comunicados'}</h4>
                <Button
                    label={vistaHistorico ? 'Ver pendientes' : 'Ver histórico'}
                    icon={vistaHistorico ? 'pi pi-inbox' : 'pi pi-history'}
                    className="p-button-sm p-button-outlined"
                    onClick={() => setVistaHistorico((v) => !v)}
                />
            </div>

            {vistaHistorico && (
                <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: '1.25rem' }}>
                    <div>
                        <label style={{ display: 'block', fontSize: '0.8rem', color: '#667085', marginBottom: 4 }}>Desde</label>
                        <Calendar value={fechaDesde} onChange={(e) => setFechaDesde(e.value)} dateFormat="dd/mm/yy" showIcon className="p-inputtext-sm" />
                    </div>
                    <div>
                        <label style={{ display: 'block', fontSize: '0.8rem', color: '#667085', marginBottom: 4 }}>Hasta</label>
                        <Calendar value={fechaHasta} onChange={(e) => setFechaHasta(e.value)} dateFormat="dd/mm/yy" showIcon className="p-inputtext-sm" />
                    </div>
                    {(fechaDesde || fechaHasta) && (
                        <Button label="Limpiar" className="p-button-sm p-button-text" onClick={() => { setFechaDesde(null); setFechaHasta(null); }} />
                    )}
                </div>
            )}

            {loading ? (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
                    <ProgressSpinner style={{ width: 56, height: 56 }} strokeWidth="4" />
                </div>
            ) : comunicados.length === 0 ? (
                <div style={{
                    background: '#fff', border: '1px solid #e4e7ec', borderRadius: 16,
                    padding: '2rem', textAlign: 'center', color: '#667085'
                }}>
                    {vistaHistorico ? 'No hay comunicados en ese rango de fechas.' : 'No tenés comunicados pendientes de leer.'}
                </div>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                    {comunicados.map((c) => (
                        <div key={c.comunicadoId} style={{
                            background: '#fff',
                            border: `1px solid ${c.urgente ? '#fecdca' : '#e4e7ec'}`,
                            borderLeft: `4px solid ${c.urgente ? '#f04438' : '#1570ef'}`,
                            borderRadius: 12,
                            padding: '1.1rem 1.25rem',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                            opacity: vistaHistorico && c.leido ? 0.7 : 1,
                        }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.5rem' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', flex: 1, flexWrap: 'wrap' }}>
                                    {c.urgente && (
                                        <span style={{
                                            background: '#fef3f2', color: '#f04438', fontWeight: 700,
                                            fontSize: '0.72rem', padding: '0.1rem 0.5rem', borderRadius: 99,
                                            border: '1px solid #fecdca', whiteSpace: 'nowrap'
                                        }}>
                                            URGENTE
                                        </span>
                                    )}
                                    <span style={{ fontWeight: 600, color: '#101828', fontSize: '0.97rem' }}>
                                        {c.asunto}
                                    </span>
                                    {vistaHistorico && c.leido && (
                                        <span style={{ fontSize: '0.72rem', color: '#12b76a', fontWeight: 600 }}>· Leído</span>
                                    )}
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginLeft: '1rem' }}>
                                    <span style={{ fontSize: '0.8rem', color: '#98a2b3', whiteSpace: 'nowrap' }}>
                                        {formatFecha(c.fecha)}
                                    </span>
                                    {!vistaHistorico && (
                                        <Button
                                            icon={marcandoId === c.comunicadoId ? 'pi pi-spin pi-spinner' : 'pi pi-check'}
                                            rounded
                                            text
                                            severity="success"
                                            style={{ width: 28, height: 28 }}
                                            disabled={marcandoId === c.comunicadoId}
                                            onClick={() => handleMarcarLeido(c.comunicadoId)}
                                            title="Marcar como leído"
                                        />
                                    )}
                                </div>
                            </div>
                            <p style={{ margin: 0, color: '#344054', fontSize: '0.9rem', lineHeight: 1.55, whiteSpace: 'pre-line' }}>
                                {c.comunicado}
                            </p>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default ComunicadosBarrio;
